import React, { useCallback, useMemo, useState } from "react";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { getLanguageCode } from "../../../../utils/languageUtility";
import { toast } from "react-toastify";
import useAuth from "../../../../hooks/useAuth";
import ResultLmcCard from "./ResultLmcCard";
import { Spinner } from "../../../atoms/Spinner";
import { ModalShell } from "../../../atoms/ModalShell";
import {
  HiBadgeCheck,
  HiCalendar,
  HiChevronDown,
  HiChevronRight,
  HiClipboardCheck,
  HiClock,
  HiCurrencyDollar,
  HiReceiptTax,
  HiStar,
  HiLightningBolt,
  HiPencilAlt,
  HiArrowLeft,
  HiOutlineAdjustments,
  HiOutlineQrcode,
  HiOutlineCog,
  HiOutlinePrinter,
  HiOutlineTrendingUp,
} from "react-icons/hi";
import {
  INormalizedLmcDataType,
  IRedirectionLink,
  ITutorialDataType,
  lmcOptionType,
} from "./types";
import PointCard from "./PointCard";
import HistoryMessageCard from "./HistoryMessageCard";
import PercentageCard from "./PercentageCard";
import IncrementalCard from "./IncrementalCard";
import AmountCard from "./AmountCard";
import DeadlineCard from "./DeadlineCard";
import VolumeCard from "./VolumeCard";
import { Helmet } from "react-helmet-async";
import { v4 as uuidv4 } from "uuid";
import RedirectionLinks from "./RedirectionLinks";
import GoogleReviewsSettings from "./GoogleReviewsSettings";
import BadgeVerification from "./BadgeVerification";
import GamingSettings from "./GamingSettings";
import GamingPreview from "./GamingPreview";
import AutoPilotSettings from "./AutoPilotSettings";
import WalletSettings from "./WalletSettings";
import {
  GamingConfiguration,
  AutoPilotConfiguration,
} from "../../../../constants/types";
import { CustomButton } from "../../../atoms/CustomButton";
import { DesktopViewRequiredMessage } from "../../../atoms/DesktopViewRequiredMessage";
import { ResponsivePreviewLayout } from "../../../atoms/ResponsivePreviewLayout";
import {
  useGetCallBot,
  useGetEntity,
  useGetLMCProcessMutation,
  useModifyWidgetCallbot,
  useUpdateLMCProcess,
  entityCache,
  useGetEntityPreferencesByPath,
} from "../../../../hooks/entity";
import CallBotSetting from "./CallBotSetting";
import LouiseBotWidget from "../../../widgets/LouiseBotWidget";
import { usePermissions, PlanTier } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { PlansModal } from "../../../modals/PlansModal";
import LoyaltyCardRenderer from "../../../atoms/LoyaltyCardRenderer";
import { useGetProcessData } from "../../../../hooks/entity";
import { languageOfCountry } from "../../../../hooks/useLanguageDetection";
import { GenericAuthProvider } from "../../../../contexts/AuthContext";
import HelpGuide from "./HelpGuide";
import parse from "html-react-parser";
import { BiLink } from "react-icons/bi";
import { LuUnplug } from "react-icons/lu";
import NoTranslate from "../../../atoms/NoTranslate";
import FeatureUpgradePrompt from "../../../atoms/FeatureUpgradePrompt";

// design-ok: teinte persistee comme donnee de carte, valeur = jeton blue-500
const COULEUR_CARTE_PAR_DEFAUT = "#3B82F6";

export type optionsType = {
  label: string;
  value:
    | "pointCard"
    | "historyMessageCard"
    | "percentageCard"
    | "incrementalCard"
    | "amountCard"
    | "deadlineCard"
    | "volumeCard"
    | null;
  image: string;
  icon: React.ReactNode;
  description: string;
  tutorials: ITutorialDataType;
};

const RECIPE_STEPS = [
  { key: "dashboard.features.lmc.page.recipe.step1", Icon: HiOutlineQrcode },
  { key: "dashboard.features.lmc.page.recipe.step2", Icon: HiOutlineCog },
  { key: "dashboard.features.lmc.page.recipe.step3", Icon: HiOutlinePrinter },
  {
    key: "dashboard.features.lmc.page.recipe.step4",
    Icon: HiOutlineTrendingUp,
  },
];

const LmcPage = () => {
  const { t, i18n } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const hasCustomLinksAccess = permissions.hasFeatureAccess("CUSTOM_LINKS");

  const lmcAvailableTypes: optionsType[] = [
    {
      label: t("dashboard.features.lmc.page.types.01"),
      value: "pointCard",
      image: "https://cdn.1cdn.app/application/UMD/25012411202556_point.svg",
      icon: (
        <HiBadgeCheck size={20} className="mt-0.5 shrink-0 text-gray-400" />
      ),
      description: parse(
        t("dashboard.features.lmc.pointCard.modalDesc")
      ) as string,
      tutorials: {
        video: {
          fr: "https://cdn.1cdn.app/application/UMD/25030919494414_point_card.mp4",
          en: "https://cdn.1cdn.app/application/UMD/25030919494414_point_card.mp4",
        },
      },
    },
    {
      label: t("dashboard.features.lmc.page.types.02"),
      value: "historyMessageCard",
      image: "https://cdn.1cdn.app/application/UMD/25012411193139_history.svg",
      icon: <HiCalendar size={20} className="mt-0.5 shrink-0 text-gray-400" />,
      description: parse(
        t("dashboard.features.lmc.historyMessageCard.modalDesc")
      ) as string,
      tutorials: {
        video: {
          fr: "https://cdn.1cdn.app/application/UMD/25030920010815_history_card.mp4",
          en: "https://cdn.1cdn.app/application/UMD/25030920010815_history_card.mp4",
        },
      },
    },
    {
      label: t("dashboard.features.lmc.page.types.03"),
      value: "incrementalCard",
      image: "https://cdn.1cdn.app/application/UMD/25012411202612_stamp.svg",
      icon: (
        <HiClipboardCheck size={20} className="mt-0.5 shrink-0 text-gray-400" />
      ),
      description: parse(
        t("dashboard.features.lmc.incrementalCard.modalDesc")
      ) as string,
      tutorials: {
        video: {
          fr: "https://cdn.1cdn.app/application/UMD/25030920031435_stamp_card.mp4",
          en: "https://cdn.1cdn.app/application/UMD/25030920031435_stamp_card.mp4",
        },
      },
    },
    {
      label: t("dashboard.features.lmc.page.types.04"),
      value: "percentageCard",
      image:
        "https://cdn.1cdn.app/application/UMD/25012411193239_percentage.svg",
      icon: (
        <HiReceiptTax size={20} className="mt-0.5 shrink-0 text-gray-400" />
      ),
      description: parse(
        t("dashboard.features.lmc.percentageCard.modalDesc")
      ) as string,
      tutorials: {
        video: {
          fr: "https://cdn.1cdn.app/application/UMD/25030920020898_percentage_card.mp4",
          en: "https://cdn.1cdn.app/application/UMD/25030920020898_percentage_card.mp4",
        },
      },
    },
    {
      label: t("dashboard.features.lmc.page.types.05"),
      value: "amountCard",
      image: "https://cdn.1cdn.app/application/UMD/2501241119327_money.svg",
      icon: (
        <HiCurrencyDollar size={20} className="mt-0.5 shrink-0 text-gray-400" />
      ),
      description: parse(
        t("dashboard.features.lmc.amountCard.modalDesc")
      ) as string,
      tutorials: {
        video: {
          fr: "https://cdn.1cdn.app/application/UMD/2503092001073_amount_card.mp4",
          en: "https://cdn.1cdn.app/application/UMD/2503092001073_amount_card.mp4",
        },
      },
    },
    {
      label: t("dashboard.features.lmc.page.types.06"),
      value: "deadlineCard",
      image:
        "https://cdn.1cdn.app/application/UMD/25012411193231_limited-time.svg",
      icon: <HiClock size={20} className="mt-0.5 shrink-0 text-gray-400" />,
      description: parse(
        t("dashboard.features.lmc.deadlineCard.modalDesc")
      ) as string,
      tutorials: {
        video: {
          fr: "https://cdn.1cdn.app/application/UMD/25030920020898_limitedtime_card.mp4",
          en: "https://cdn.1cdn.app/application/UMD/25030920020898_limitedtime_card.mp4",
        },
      },
    },
    {
      label: t("dashboard.features.lmc.page.types.07"),
      value: "volumeCard",
      image: "https://cdn.1cdn.app/application/UMD/25012411193146_group.svg",
      icon: <HiStar size={20} className="mt-0.5 shrink-0 text-gray-400" />,
      description: parse(
        t("dashboard.features.lmc.volumeCard.modalDesc")
      ) as string,
      tutorials: {
        video: {
          fr: "https://cdn.1cdn.app/application/UMD/25030920031584_volume_card.mp4",
          en: "https://cdn.1cdn.app/application/UMD/25030920031584_volume_card.mp4",
        },
      },
    },
  ];

  const lmcLabelMapper = useMemo(() => {
    return {
      POINT_CARD: "pointCard",
      HIST_MESSAGE_CARD: "historyMessageCard",
      PERCENTAGE_CARD: "percentageCard",
      INCREMENTAL_CARD: "incrementalCard",
      AMOUNT_CARD: "amountCard",
      DEADLINE_CARD: "deadlineCard",
      VOLUME_CARD: "volumeCard",
    };
  }, []);

  const lmcLabelMapperReverse = {
    pointCard: "POINT_CARD",
    historyMessageCard: "HIST_MESSAGE_CARD",
    percentageCard: "PERCENTAGE_CARD",
    incrementalCard: "INCREMENTAL_CARD",
    amountCard: "AMOUNT_CARD",
    deadlineCard: "DEADLINE_CARD",
    volumeCard: "VOLUME_CARD",
  };

  const [isInitiating, setIsInitiating] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedLmcForCreation, setSelectedLmcForCreation] =
    useState<optionsType["value"]>();
  const [hasLmc, setHasLmc] = useState<boolean>(false);
  const [currentPlaygroundData, setCurrentPlaygroundData] = useState<{
    type: lmcOptionType;
    data: INormalizedLmcDataType;
  }>();
  const [redirectionLinks, setRedirectionLinks] = useState<
    Array<IRedirectionLink>
  >([{ id: uuidv4(), label: "", link: "" }]);

  const [processId, setProcessId] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [isQuickSetupLoading, setIsQuickSetupLoading] = useState(false);
  const [hasRecreated, setHasRecreated] = useState(false);
  const [showCustomLinksUpgradeModal, setShowCustomLinksUpgradeModal] =
    useState(false);
  const [expandedSection, setExpandedSection] = useState<
    "qr" | "links" | "integrations" | "ai-addons" | null
  >("qr");

  const [liveGamingConfig, setLiveGamingConfig] = useState<
    GamingConfiguration | undefined
  >();

  const {
    mutate: getLMCData,
    isLoading: isGetting,
    isError: isErrorGetting,
  } = useGetLMCProcessMutation();

  const {
    data: entityData,
    isSuccess: entityIsSuccess,
    isLoading: entityIsLoading,
    isError: entityIsError,
    refetch: entityRefetch,
  } = useGetEntity(customer_identification, user.entity_identification);

  const {
    data: res,
    isError,
    isLoading,
    isSuccess,
    refetch,
  } = useGetEntityPreferencesByPath(
    customer_identification,
    user.entity_identification,
    "lmc_form",
    {
      onSuccess(data) {
        if (editMode) {
          return;
        }

        if (data?.lmc_form) {
          setRedirectionLinks(data?.lmc_form?.redirectionLinks || []);
          if (data?.lmc_form?.lmc_type) {
            setHasLmc(true);

            getLMCData(
              {
                customer_identification: customer_identification,
                entity_identification: user.entity_identification,
              },
              {
                onSuccess: (value) => {
                  setProcessId(value?.data?.element?.process_identification);
                },
              }
            );

            setSelectedLmcForCreation(
              lmcLabelMapper[
                data.lmc_form.lmc_type as lmcOptionType
              ] as optionsType["value"]
            );

            setCurrentPlaygroundData({
              type: data.lmc_form.lmc_type as lmcOptionType,
              data: data.lmc_form,
            });
            setIsInitiating(false);
          } else {
            setHasLmc(false);
            setIsInitiating(false);
          }
        } else {
          setHasLmc(false);
          setIsInitiating(false);
        }
      },
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);

        setIsInitiating(false);
      },
    }
  );

  const { mutateAsync: updateLMCDataAsync } = useUpdateLMCProcess();
  const {
    mutate: modifyLMCWidget,
    mutateAsync: modifyLMCWidgetAsync,
    isLoading: modifyCallbotLoading,
  } = useModifyWidgetCallbot();

  const { data: data_bot, isLoading: callbotLoading } = useGetCallBot(
    user.entity_identification,
    customer_identification,
    res?.lmc_form?.widget?.bot_id,
    {
      enabled:
        (res?.lmc_form?.widget?.bot_id !== "" &&
          res?.lmc_form?.widget?.bot_id !== undefined &&
          !modifyCallbotLoading) ||
        false,
      onError: (err) => {
        if (err?.response?.status === 400 && !hasRecreated) {
          setHasRecreated(true);
          reCreateWidget();
        }
      },
    }
  );

  const {
    data: processData,
    isLoading: isLoadingProcessData,
    refetch: refetchProcessData,
  } = useGetProcessData(processId, {
    enabled: !!processId,
  });

  const handleQuickSetup = useCallback(async () => {
    setIsQuickSetupLoading(true);

    const isFrench = getLanguageCode(i18n.language) === "fr";
    const defaultLabels = {
      cardTitle: isFrench ? "Carte de Fidélité" : "Loyalty Card",
      cardDescription: isFrench
        ? "Collectez des points à chaque achat"
        : "Collect points with every purchase",
    };

    const defaultPointCardData: INormalizedLmcDataType = {
      lmc_type: "POINT_CARD",
      scanner_auto_refresh: false,
      pre_defined_value: {
        type: "point",
        values: [1, 5, 10],
        default: 1,
        currency: null,
      },
      comment: "",
      package: 20,
      options: {
        cardTitle: defaultLabels.cardTitle,
        cardDescription: defaultLabels.cardDescription,
        pointsToEarn: "1",
        moneyToSpend: "1",
        bonusValue: "5",
        bonusType: "%",
        businessName: "",
        businessDescription: "",
        businessWebsite: "",
        businessPhone: "",
        businessEmail: "",
        businessAddress: "",
        businessCity: "",
        businessZip: "",
        businessCountry: "",
        cardColor: COULEUR_CARTE_PAR_DEFAUT,
        cardType: "POINT_CARD",
        logo: "",
        language: i18n.language,
        login_form: {},
      },
    };

    try {
      await updateLMCDataAsync(
        {
          customer_identification: customer_identification,
          user_identification: user.user_identification,
          entity_identification: user.entity_identification,
          data: defaultPointCardData,
        },
        {
          onSuccess: (response: AxiosResponse<any, any>) => {
            setSelectedLmcForCreation("pointCard");
            setCurrentPlaygroundData({
              type: "POINT_CARD",
              data: defaultPointCardData,
            });
            setHasLmc(true);
            setEditMode(false);

            getLMCData(
              {
                customer_identification: customer_identification,
                entity_identification: user.entity_identification,
              },
              {
                onSuccess: (value) => {
                  setProcessId(value?.data?.element?.process_identification);
                },
              }
            );

            const successMessage = isFrench
              ? "🎉 Votre programme de fidélité est prêt ! Vous pouvez l'utiliser dès maintenant."
              : "🎉 Your loyalty program is ready! You can start using it right away.";
            toast.success(successMessage);
          },
          onError: (error) => {
            const errorMessage = isFrench
              ? "Échec de la création du programme de fidélité. Veuillez réessayer."
              : "Failed to create loyalty program. Please try again.";
            toast.error(errorMessage);
          },
        }
      );
    } catch (error) {
      console.error("Quick setup error:", error);
    } finally {
      setIsQuickSetupLoading(false);
    }
  }, [
    i18n.language,
    updateLMCDataAsync,
    customer_identification,
    user.user_identification,
    user.entity_identification,
    getLMCData,
  ]);

  const handleModifyLmcInEntityPreferences = useCallback(
    async (type: lmcOptionType, data: INormalizedLmcDataType) => {
      const { lmc_form } = res;
      await updateLMCDataAsync(
        {
          customer_identification: customer_identification,
          user_identification: user.user_identification,
          entity_identification: user.entity_identification,
          data: {
            ...res?.lmc_form,
            ...data,
            login_form: lmc_form?.login_form || {},
            language: i18n.language,
          },
        },
        {
          onSuccess: (res: AxiosResponse<any, any>) => {
            setEditMode(false);
            toast.success(t("dashboard.features.lmc.page.saveSuccess"));
            if (processId) {
              entityCache.delete(`entity_process_${processId}`);
              entityCache.delete(`entity_process_ui_${processId}`);

              refetchProcessData();
              refetch();
            }
          },
        }
      );
    },
    [
      customer_identification,
      i18n.language,
      res,
      updateLMCDataAsync,
      user.entity_identification,
      user.user_identification,
      processId,
      refetchProcessData,
      t,
    ]
  );

  const handleCancelEditMode = useCallback(() => {
    if (isSuccess && res?.lmc_form) {
      setHasLmc(true);

      setSelectedLmcForCreation(
        lmcLabelMapper[
          res?.lmc_form?.lmc_type as lmcOptionType
        ] as optionsType["value"]
      );

      setCurrentPlaygroundData({
        type: res?.lmc_form?.lmc_type as lmcOptionType,
        data: res?.lmc_form,
      });
    } else {
      setHasLmc(false);

      setSelectedLmcForCreation(undefined);

      setCurrentPlaygroundData(undefined);
    }

    setEditMode(false);
  }, [isSuccess, lmcLabelMapper, res?.lmc_form]);

  const renderProperForm = useCallback(() => {
    switch (selectedLmcForCreation) {
      case "pointCard":
        return (
          <PointCard
            key="pointCard"
            onSubmitCardData={handleModifyLmcInEntityPreferences}
            optionData={
              res?.lmc_form?.lmc_type === "POINT_CARD" ? res?.lmc_form : null
            }
            playgroundDataHandler={setCurrentPlaygroundData}
            handleCancelEdit={handleCancelEditMode}
            tutorial={
              lmcAvailableTypes.find(
                (item) => item.value === selectedLmcForCreation
              )?.tutorials
            }
          />
        );
      case "historyMessageCard":
        return (
          <HistoryMessageCard
            key="historyMessageCard"
            onSubmitCardData={handleModifyLmcInEntityPreferences}
            optionData={
              res?.lmc_form?.lmc_type === "HIST_MESSAGE_CARD"
                ? res?.lmc_form
                : null
            }
            playgroundDataHandler={setCurrentPlaygroundData}
            handleCancelEdit={handleCancelEditMode}
            tutorial={
              lmcAvailableTypes.find(
                (item) => item.value === selectedLmcForCreation
              )?.tutorials
            }
          />
        );
      case "percentageCard":
        return (
          <PercentageCard
            key="percentageCard"
            onSubmitCardData={handleModifyLmcInEntityPreferences}
            optionData={
              res?.lmc_form?.lmc_type === "PERCENTAGE_CARD"
                ? res?.lmc_form
                : null
            }
            playgroundDataHandler={setCurrentPlaygroundData}
            handleCancelEdit={handleCancelEditMode}
            tutorial={
              lmcAvailableTypes.find(
                (item) => item.value === selectedLmcForCreation
              )?.tutorials
            }
          />
        );
      case "incrementalCard":
        return (
          <IncrementalCard
            key="incrementalCard"
            onSubmitCardData={handleModifyLmcInEntityPreferences}
            optionData={
              res?.lmc_form?.lmc_type === "INCREMENTAL_CARD"
                ? res?.lmc_form
                : null
            }
            playgroundDataHandler={setCurrentPlaygroundData}
            handleCancelEdit={handleCancelEditMode}
            tutorial={
              lmcAvailableTypes.find(
                (item) => item.value === selectedLmcForCreation
              )?.tutorials
            }
          />
        );
      case "amountCard":
        return (
          <AmountCard
            key="amountCard"
            onSubmitCardData={handleModifyLmcInEntityPreferences}
            optionData={
              res?.lmc_form?.lmc_type === "AMOUNT_CARD" ? res?.lmc_form : null
            }
            playgroundDataHandler={setCurrentPlaygroundData}
            handleCancelEdit={handleCancelEditMode}
            tutorial={
              lmcAvailableTypes.find(
                (item) => item.value === selectedLmcForCreation
              )?.tutorials
            }
          />
        );
      case "deadlineCard":
        return (
          <DeadlineCard
            key="deadlineCard"
            onSubmitCardData={handleModifyLmcInEntityPreferences}
            optionData={
              res?.lmc_form?.lmc_type === "DEADLINE_CARD" ? res?.lmc_form : null
            }
            playgroundDataHandler={setCurrentPlaygroundData}
            handleCancelEdit={handleCancelEditMode}
            tutorial={
              lmcAvailableTypes.find(
                (item) => item.value === selectedLmcForCreation
              )?.tutorials
            }
          />
        );
      case "volumeCard":
        return (
          <VolumeCard
            key="volumeCard"
            onSubmitCardData={handleModifyLmcInEntityPreferences}
            optionData={
              res?.lmc_form?.lmc_type === "VOLUME_CARD" ? res?.lmc_form : null
            }
            playgroundDataHandler={setCurrentPlaygroundData}
            handleCancelEdit={handleCancelEditMode}
            tutorial={
              lmcAvailableTypes.find(
                (item) => item.value === selectedLmcForCreation
              )?.tutorials
            }
          />
        );
      default:
        return null;
    }
  }, [
    selectedLmcForCreation,
    handleModifyLmcInEntityPreferences,
    res?.lmc_form,
    handleCancelEditMode,
    lmcAvailableTypes,
  ]);

  const handleModifyRedirectLinks = useCallback(
    async (data: Array<IRedirectionLink>) => {
      let inputObject;
      if (res?.lmc_form !== undefined) {
        inputObject = {
          ...res?.lmc_form,
          redirectionLinks: [...data],
        };
      } else {
        inputObject = {
          redirectionLinks: [...data],
        };
      }
      await updateLMCDataAsync(
        {
          customer_identification: customer_identification,
          user_identification: user.user_identification,
          entity_identification: user.entity_identification,
          data: inputObject,
        },
        {
          onSuccess: () => {
            if (processId) {
              entityCache.delete(`entity_process_${processId}`);
              entityCache.delete(`entity_process_ui_${processId}`);
              refetchProcessData();
            }
          },
        }
      );
    },
    [
      customer_identification,
      res?.lmc_form,
      updateLMCDataAsync,
      user.entity_identification,
      user.user_identification,
      processId,
      refetchProcessData,
    ]
  );

  const handleModifyGoogleReviews = useCallback(
    async (googleReviewsData: any) => {
      let inputObject;
      if (res?.lmc_form !== undefined) {
        inputObject = {
          ...res?.lmc_form,
          ...googleReviewsData,
        };
      } else {
        inputObject = {
          ...googleReviewsData,
        };
      }
      await updateLMCDataAsync(
        {
          customer_identification: customer_identification,
          user_identification: user.user_identification,
          entity_identification: user.entity_identification,
          data: inputObject,
        },
        {
          onSuccess: () => {
            if (processId) {
              entityCache.delete(`entity_process_${processId}`);
              entityCache.delete(`entity_process_ui_${processId}`);
              refetchProcessData();
            }
          },
        }
      );
    },
    [
      customer_identification,
      res?.lmc_form,
      updateLMCDataAsync,
      user.entity_identification,
      user.user_identification,
      processId,
      refetchProcessData,
    ]
  );

  const handleModifyGaming = useCallback(
    async (gamingConfig: GamingConfiguration) => {
      let inputObject;
      if (res?.lmc_form !== undefined) {
        inputObject = {
          ...res?.lmc_form,
          gaming: gamingConfig,
        };
      } else {
        inputObject = {
          gaming: gamingConfig,
        };
      }
      await updateLMCDataAsync(
        {
          customer_identification: customer_identification,
          user_identification: user.user_identification,
          entity_identification: user.entity_identification,
          data: inputObject,
        },
        {
          onSuccess: () => {
            if (processId) {
              entityCache.delete(`entity_process_${processId}`);
              entityCache.delete(`entity_process_ui_${processId}`);
              refetchProcessData();
            }
          },
        }
      );
    },
    [
      customer_identification,
      res?.lmc_form,
      updateLMCDataAsync,
      user.entity_identification,
      user.user_identification,
      processId,
      refetchProcessData,
    ]
  );

  const handleModifyAutoPilot = useCallback(
    async (autoPilotConfig: AutoPilotConfiguration) => {
      let inputObject;
      if (res?.lmc_form !== undefined) {
        inputObject = {
          ...res?.lmc_form,
          auto_pilot: autoPilotConfig,
        };
      } else {
        inputObject = {
          auto_pilot: autoPilotConfig,
        };
      }
      const response = await updateLMCDataAsync(
        {
          customer_identification: customer_identification,
          user_identification: user.user_identification,
          entity_identification: user.entity_identification,
          data: inputObject,
        },
        {
          onSuccess: () => {
            if (processId) {
              entityCache.delete(`entity_process_${processId}`);
              entityCache.delete(`entity_process_ui_${processId}`);
              refetchProcessData();
            }
          },
        }
      );
      return response?.data?.element?.lmcData?.auto_pilot as
        | AutoPilotConfiguration
        | undefined;
    },
    [
      customer_identification,
      res?.lmc_form,
      updateLMCDataAsync,
      user.entity_identification,
      user.user_identification,
      processId,
      refetchProcessData,
    ]
  );

  const handleModifyWalletSettings = useCallback(
    async (walletData: any) => {
      let inputObject;
      if (res?.lmc_form !== undefined) {
        inputObject = {
          ...res?.lmc_form,
          ...walletData,
        };
      } else {
        inputObject = {
          ...walletData,
        };
      }
      await updateLMCDataAsync(
        {
          customer_identification: customer_identification,
          user_identification: user.user_identification,
          entity_identification: user.entity_identification,
          data: inputObject,
        },
        {
          onSuccess: () => {
            if (processId) {
              entityCache.delete(`entity_process_${processId}`);
              entityCache.delete(`entity_process_ui_${processId}`);
              refetchProcessData();
            }
          },
        }
      );
    },
    [
      customer_identification,
      res?.lmc_form,
      updateLMCDataAsync,
      user.entity_identification,
      user.user_identification,
      processId,
      refetchProcessData,
    ]
  );

  const handleModifyWidget = useCallback(
    async (data: any) => {
      const customInstructions = data?.instructions
        ? data.instructions
            .split("\n")
            .filter((i: string) => i.trim().length > 0)
        : [];

      const input = {
        enabled: data.enabled,
        custom_instructions: customInstructions,
      };

      await modifyLMCWidgetAsync(
        {
          customer_identification: customer_identification,
          entity_identification: user.entity_identification,
          data: input,
        },
        {
          onSuccess: () => {
            if (processId) {
              entityCache.delete(`entity_process_${processId}`);
              entityCache.delete(`entity_process_ui_${processId}`);
              refetchProcessData();
            }
          },
        }
      );
    },
    [
      modifyLMCWidgetAsync,
      customer_identification,
      user.entity_identification,
      processId,
      refetchProcessData,
    ]
  );

  function reCreateWidget() {
    const input = {
      enabled: res?.lmc_form?.widget?.enabled,
      custom_instructions: [],
    };
    modifyLMCWidget({
      customer_identification: customer_identification,
      entity_identification: user.entity_identification,
      data: input,
    });
  }

  let content;

  if (isLoading || isInitiating || entityIsLoading)
    content = (
      <div className="mt-4" translate="no">
        <Spinner size="sm" />
      </div>
    );

  if (isError || entityIsError)
    content = (
      <div className="mt-4">
        <CustomButton
          onClick={() => {
            refetch();
            entityRefetch();
          }}
          title="Reload"
        />
      </div>
    );

  if (isSuccess && entityIsSuccess) {
    content = (
      <ResponsivePreviewLayout
        configColSpan={5}
        previewColSpan={7}
        hasPreviewContent={hasLmc && !!selectedLmcForCreation}
        previewContent={
          <div className="w-full h-full p-1 bg-gray-100 flex-col justify-start items-center inline-flex">
            {!isInitiating && !hasLmc && !selectedLmcForCreation && (
              <React.Fragment>
                <div className="self-stretch grow shrink basis-0 rounded-xl border border-gray-200 bg-white p-6 sm:p-8">
                  <div className="mx-auto max-w-2xl text-center">
                    <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                      <HiLightningBolt className="h-6 w-6" />
                    </div>
                    <h2 className="text-lg font-bold leading-tight text-gray-900 sm:text-xl">
                      {t("dashboard.features.lmc.page.recipe.title")}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                      {t("dashboard.features.lmc.page.recipe.subtitle")}
                    </p>
                  </div>

                  <ol className="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
                    {RECIPE_STEPS.map((step, index) => (
                      <li
                        key={step.key}
                        className="relative rounded-xl border border-gray-200 bg-gray-50 p-4 pt-6"
                      >
                        <span className="absolute -top-3 left-4 inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white shadow-sm">
                          {index + 1}
                        </span>
                        <div className="flex items-center gap-2">
                          <step.Icon className="h-5 w-5 shrink-0 text-blue-600" />
                          <h3 className="text-sm font-semibold leading-tight text-gray-900">
                            {t(step.key)}
                          </h3>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </React.Fragment>
            )}

            {selectedLmcForCreation && (
              <div className="w-full h-full">
                {editMode && currentPlaygroundData && (
                  <div className="h-full">
                    <HelpGuide
                      key={currentPlaygroundData.type}
                      selectedLmcForCreation={selectedLmcForCreation}
                      currentPlaygroundData={currentPlaygroundData}
                      processId={processId}
                    />
                  </div>
                )}

                {!editMode && (
                  <div className="w-full h-full flex flex-col gap-6 overflow-y-auto">
                    <h2 className="self-stretch text-lg font-semibold text-gray-900">
                      {t("dashboard.features.lmc.page.key12")}
                    </h2>

                    <div className="block xs:hidden">
                      <DesktopViewRequiredMessage />
                    </div>

                    {(liveGamingConfig?.enabled ||
                      res?.lmc_form?.gaming?.enabled) && (
                      <div className="w-full hidden xs:flex justify-center">
                        <div className="w-full max-w-md">
                          <GamingPreview
                            gamingConfig={
                              liveGamingConfig || res?.lmc_form?.gaming
                            }
                          />
                        </div>
                      </div>
                    )}

                    {processId && processData && !isLoadingProcessData && (
                      <div className="w-full hidden xs:block">
                        <div className="pointer-events-none [&_*]:pointer-events-none [&_iframe]:pointer-events-none">
                          <GenericAuthProvider>
                            <LoyaltyCardRenderer
                              customerIdentification={
                                processData?.entity_data?.owner_data
                                  ?.user_identification || ""
                              }
                              entityIdentification={
                                processData?.entity_data
                                  ?.entity_identification || ""
                              }
                              entity={{
                                name:
                                  processData?.entity_data?.entity_name ||
                                  "Business",
                                description:
                                  processData?.entity_data?.entity_description,
                                logo: processData?.entity_data?.entity_logo,
                                accentColor:
                                  processData?.entity_data?.accent_color,
                              }}
                              program={{
                                type:
                                  processData?.process_data?.lmc_type ||
                                  "POINT_CARD",
                                title: processData?.process_data?.title,
                                comment: processData?.process_data?.comment,
                                color: processData?.process_data?.color,
                                bonusText:
                                  processData?.process_data?.bonus_text,
                                expiryText:
                                  processData?.process_data?.expiry_text,
                                redirectionLinks:
                                  processData?.process_data?.redirectionLinks,
                                badges: processData?.process_data?.badges,
                              }}
                              member={{
                                id:
                                  processData?.entity_data?.owner_data
                                    ?.user_identification || "",
                                fullName:
                                  `${
                                    processData?.entity_data?.owner_data
                                      ?.first_name || ""
                                  } ${
                                    processData?.entity_data?.owner_data
                                      ?.last_name || ""
                                  }`.trim() || "Member",
                                avatar:
                                  processData?.entity_data?.owner_data?.avatar,
                              }}
                              qr={{
                                data: `${process.env.REACT_APP_HOST_URL}/loyalty-program/${processId}`,
                                isValid: true,
                              }}
                              shareUrl={`${process.env.REACT_APP_HOST_URL}/loyalty-program/${processId}`}
                              rewardPath={`/loyalty-program/${processId}/rewards`}
                              googleReview={{
                                enabled:
                                  processData?.process_data?.google_places
                                    ?.review_link_enabled,
                                place_id:
                                  processData?.process_data?.google_places
                                    ?.google_place_id,
                              }}
                              widget={{
                                enabled: Boolean(
                                  res?.lmc_form?.widget?.enabled
                                ),
                                bot_id: res?.lmc_form?.widget?.bot_id,
                                mode: "inline",
                              }}
                            />
                          </GenericAuthProvider>
                        </div>
                      </div>
                    )}

                    {processId && isLoadingProcessData && (
                      <div className="w-full hidden xs:flex justify-center items-center py-8">
                        <Spinner size="lg" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        }
      >
        <div className="bg-white w-full h-full p-4 sm:p-6 flex-col justify-start items-start gap-6 inline-flex overflow-y-auto overflow-x-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 w-full">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-gray-900">
                {t("dashboard.features.lmc.page.setup.friendlyTitle")}
              </h1>
              <p className="mt-0.5 text-sm text-gray-500">
                {t("dashboard.features.lmc.page.setup.friendlySubtitle")}
              </p>
            </div>
          </div>

          {!isInitiating && !hasLmc && !selectedLmcForCreation && (
            <React.Fragment>
              <p className="w-full text-sm text-gray-600">
                {t("dashboard.features.lmc.page.setup.chooseHowToStart")}
              </p>

              <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col rounded-xl border border-blue-100 bg-blue-50/60 p-5">
                  <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-blue-100 bg-white text-blue-600">
                    <HiLightningBolt className="h-5 w-5" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900">
                    {t("dashboard.features.lmc.quickSetup.title")}
                  </h3>
                  <p className="mt-1.5 flex-1 text-sm text-gray-600">
                    <span className="font-medium text-gray-800">
                      {t("dashboard.features.lmc.quickSetup.readyLine")}
                    </span>{" "}
                    {t("dashboard.features.lmc.quickSetup.descriptionLine")}
                  </p>
                  <CustomButton
                    className="mt-4 w-full"
                    title={
                      isQuickSetupLoading
                        ? t("dashboard.features.lmc.quickSetup.creating")
                        : t("dashboard.features.lmc.quickSetup.quickCta")
                    }
                    onClick={handleQuickSetup}
                    variant="primary"
                    disabled={
                      isQuickSetupLoading ||
                      !permissions.can(Cap.LOYALTY, Level.WRITE)
                    }
                  />
                </div>

                <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-5">
                  <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-600">
                    <HiOutlineAdjustments className="h-5 w-5" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900">
                    {t("dashboard.features.lmc.quickSetup.advancedTitle")}
                  </h3>
                  <p className="mt-1.5 flex-1 text-sm text-gray-600">
                    <span className="font-medium text-gray-800">
                      {t("dashboard.features.lmc.page.setup.fullControl")}
                    </span>{" "}
                    {t("dashboard.features.lmc.page.setup.pickFromTypes")}
                  </p>
                  <CustomButton
                    className="mt-4 w-full"
                    title={t("dashboard.features.lmc.quickSetup.advancedCta")}
                    onClick={() => setShowModal(true)}
                    variant="secondary"
                    disabled={!permissions.can(Cap.LOYALTY, Level.WRITE)}
                  />
                </div>
              </div>
            </React.Fragment>
          )}

          {!isInitiating && selectedLmcForCreation && (
            <React.Fragment>
              {!editMode && hasLmc && (
                <React.Fragment>
                  <div className="w-full space-y-3">
                    <h2 className="text-lg font-semibold text-gray-900">
                      1. {t("dashboard.features.lmc.page.setup.startHere")}
                    </h2>
                    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                      <div className="w-full p-5">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="w-9 h-9 bg-gray-50 text-gray-600 rounded-md flex items-center justify-center flex-shrink-0">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <h3 className="text-sm font-semibold text-gray-900">
                              {t(
                                "dashboard.features.lmc.page.setup.getYourQRCode"
                              )}
                            </h3>
                            <p className="mt-0.5 text-xs text-gray-500">
                              {t(
                                "dashboard.features.lmc.page.setup.printAndDisplay"
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="">
                          {isGetting ? (
                            <div className="flex justify-center py-8">
                              <Spinner size="md" />
                            </div>
                          ) : isErrorGetting ? (
                            <div className="text-center py-4">
                              <CustomButton
                                className="mx-auto"
                                onClick={() => {
                                  getLMCData({
                                    customer_identification:
                                      customer_identification,
                                    entity_identification:
                                      user.entity_identification,
                                  });
                                }}
                                title={t("dashboard.features.lmc.page.key10")}
                              />
                            </div>
                          ) : (
                            <ResultLmcCard
                              lmc={res?.lmc_form?.lmc_type as lmcOptionType}
                              processId={processId}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <h2 className="w-full text-lg font-semibold text-gray-900">
                    2. {t("dashboard.features.lmc.page.setup.yourCampaign")}
                  </h2>
                </React.Fragment>
              )}

              {editMode && (
                <CustomButton
                  type="button"
                  variant="none"
                  size="sm"
                  onClick={() => setEditMode(false)}
                  className="group mb-4 self-start px-0 font-medium text-gray-600 enabled:hover:text-gray-900"
                  icon={
                    <HiArrowLeft
                      size={20}
                      className="transition-transform group-hover:-translate-x-1"
                    />
                  }
                  title={t("dashboard.features.lmc.pointCard.backButton")}
                />
              )}

              <div className="w-full flex-col justify-start items-start gap-2 inline-flex">
                {editMode ? (
                  <div
                    onClick={() =>
                      permissions.can(Cap.LOYALTY, Level.WRITE) &&
                      setShowModal(true)
                    }
                    className={`self-stretch justify-start items-start gap-5 inline-flex ${
                      permissions.can(Cap.LOYALTY, Level.WRITE)
                        ? "cursor-pointer"
                        : "cursor-not-allowed opacity-60"
                    }`}
                  >
                    <div className="grow shrink basis-0 px-3 py-2 bg-white rounded-md border border-gray-300 justify-between items-center flex">
                      <div className="flex items-center gap-3 flex-1">
                        <img
                          alt={
                            lmcAvailableTypes.find(
                              (item) => item.value === selectedLmcForCreation
                            )?.label
                          }
                          src={
                            lmcAvailableTypes.find(
                              (item) => item.value === selectedLmcForCreation
                            )?.image
                          }
                          className="w-16 h-12 object-cover bg-gray-800 rounded border border-gray-300"
                        />
                        <div className="text-gray-700 text-sm font-medium font-figtree leading-tight">
                          {
                            lmcAvailableTypes.find(
                              (item) => item.value === selectedLmcForCreation
                            )?.label
                          }
                        </div>
                      </div>
                      <div className="w-5 h-5 relative">
                        <HiChevronDown className="text-gray-300" size={20} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="self-stretch justify-start items-start gap-5 inline-flex">
                    <div className="grow shrink basis-0 bg-white rounded-lg border border-gray-200 overflow-hidden transition-colors hover:border-gray-300">
                      <div className="p-4 flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                          <img
                            alt={
                              lmcAvailableTypes.find(
                                (item) => item.value === selectedLmcForCreation
                              )?.label
                            }
                            src={
                              lmcAvailableTypes.find(
                                (item) => item.value === selectedLmcForCreation
                              )?.image
                            }
                            className="w-16 h-12 object-cover bg-gray-800 rounded-md border border-gray-200"
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900">
                              {
                                lmcAvailableTypes.find(
                                  (item) =>
                                    item.value === selectedLmcForCreation
                                )?.label
                              }
                            </div>
                            <div className="mt-0.5 text-xs text-gray-500">
                              {t("dashboard.features.lmc.page.key3")}
                            </div>
                          </div>
                        </div>
                        {permissions.can(Cap.LOYALTY, Level.WRITE) && (
                          <NoTranslate>
                            <div className="flex">
                              <CustomButton
                                variant="secondary"
                                size="sm"
                                onClick={() => setEditMode(true)}
                                comp={
                                  <>
                                    <HiPencilAlt
                                      size={14}
                                      className="text-gray-400"
                                    />
                                    {t("dashboard.features.lmc.page.key5")}
                                  </>
                                }
                              />
                            </div>
                          </NoTranslate>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {editMode ? (
                <React.Fragment>
                  <div className="flex flex-col" translate="no">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {t(
                        "dashboard.features.lmc.page.setup.configureYourCard",
                        {
                          cardType: lmcAvailableTypes.find(
                            (item) => item.value === selectedLmcForCreation
                          )?.label,
                        }
                      )}
                    </h2>
                    <div className="mt-0.5 text-sm text-gray-500">
                      {
                        parse(
                          t(
                            `dashboard.features.lmc.${selectedLmcForCreation}.alertDesc`
                          )
                        ) as string
                      }
                    </div>
                  </div>

                  {renderProperForm()}
                </React.Fragment>
              ) : null}
            </React.Fragment>
          )}

          {hasLmc && !editMode && (
            <div className="w-full space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">
                3. {t("dashboard.features.lmc.page.setup.optionalExtras")}
              </h2>

              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <button
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === "links" ? null : "links"
                    )
                  }
                  className="w-full flex items-center justify-between gap-3 p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="w-9 h-9 bg-gray-50 text-gray-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <BiLink size={18} />
                    </div>
                    <div className="min-w-0 text-left">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {t("dashboard.features.lmc.page.setup.addQuickLinks")}
                      </h3>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {t(
                          "dashboard.features.lmc.page.setup.linkToSocialMenu"
                        )}{" "}
                        ({redirectionLinks.filter((l) => l.link).length}{" "}
                        {t("dashboard.features.lmc.page.setup.added")})
                      </p>
                    </div>
                  </div>
                  <HiChevronDown
                    className={`shrink-0 text-gray-400 transition-transform ${
                      expandedSection === "links" ? "rotate-180" : ""
                    }`}
                    size={20}
                  />
                </button>
                {expandedSection === "links" && (
                  <div className="p-4 pt-0 border-t border-gray-100">
                    {hasCustomLinksAccess ? (
                      <RedirectionLinks
                        OnSubmit={handleModifyRedirectLinks}
                        data={redirectionLinks}
                        maxLinks={5}
                      />
                    ) : (
                      <>
                        <FeatureUpgradePrompt
                          featureName={
                            t(
                              "dashboard.features.lmc.page.setup.addQuickLinks"
                            ) as string
                          }
                          featureDescription={
                            t(
                              "dashboard.features.lmc.redirectLinks.upgradeDescription"
                            ) as string
                          }
                          benefits={[
                            t(
                              "dashboard.features.lmc.redirectLinks.benefits.social"
                            ) as string,
                            t(
                              "dashboard.features.lmc.redirectLinks.benefits.website"
                            ) as string,
                            t(
                              "dashboard.features.lmc.redirectLinks.benefits.menu"
                            ) as string,
                          ]}
                          icon={BiLink}
                          iconTheme="blue"
                          onUpgrade={() => setShowCustomLinksUpgradeModal(true)}
                        />
                        <PlansModal
                          showPlansModal={showCustomLinksUpgradeModal}
                          setShowPlansModal={setShowCustomLinksUpgradeModal}
                          targetPlan={PlanTier.PREMIUM}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <button
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === "integrations" ? null : "integrations"
                    )
                  }
                  className="w-full flex items-center justify-between gap-3 p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="w-9 h-9 bg-gray-50 text-gray-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <LuUnplug size={18} />
                    </div>
                    <div className="min-w-0 text-left">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {t("dashboard.features.lmc.page.setup.powerfulAddOns")}
                      </h3>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {t("dashboard.features.lmc.page.setup.reviewsBotBadge")}
                      </p>
                    </div>
                  </div>
                  <HiChevronDown
                    className={`shrink-0 text-gray-400 transition-transform ${
                      expandedSection === "integrations" ? "rotate-180" : ""
                    }`}
                    size={20}
                  />
                </button>
                {expandedSection === "integrations" && (
                  <div className="p-4 pt-0 space-y-4 border-t border-gray-100">
                    <GoogleReviewsSettings
                      data={{
                        google_places:
                          res?.lmc_form?.google_places || undefined,
                        badges: res?.lmc_form?.badges,
                      }}
                      onSubmit={handleModifyGoogleReviews}
                    />
                    <div className="self-stretch h-px bg-gray-200 block mt-6" />

                    <WalletSettings
                      data={{
                        wallet_settings: res?.lmc_form?.wallet_settings,
                      }}
                      onSubmit={handleModifyWalletSettings}
                    />
                    <div className="self-stretch h-px bg-gray-200 block mt-6" />
                    <GamingSettings
                      data={{
                        gaming: res?.lmc_form?.gaming,
                      }}
                      onSubmit={handleModifyGaming}
                      onChange={setLiveGamingConfig}
                    />
                    <div className="self-stretch h-px bg-gray-200 block mt-6" />
                    {permissions.can(Cap.LOYALTY, Level.WRITE) && (
                      <BadgeVerification badges={res?.lmc_form?.badges} />
                    )}
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <button
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === "ai-addons" ? null : "ai-addons"
                    )
                  }
                  className="w-full flex items-center justify-between gap-3 p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="w-9 h-9 bg-gray-50 text-gray-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <HiLightningBolt size={18} />
                    </div>
                    <div className="min-w-0 text-left">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {t("dashboard.features.lmc.page.setup.aiAddons")}
                      </h3>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {t("dashboard.features.lmc.page.setup.aiAddonsDesc")}
                      </p>
                    </div>
                  </div>
                  <HiChevronDown
                    className={`shrink-0 text-gray-400 transition-transform ${
                      expandedSection === "ai-addons" ? "rotate-180" : ""
                    }`}
                    size={20}
                  />
                </button>
                {expandedSection === "ai-addons" && (
                  <div className="p-4 pt-0 space-y-4 border-t border-gray-100">
                    <CallBotSetting
                      initialEnabled={res?.lmc_form?.widget?.enabled}
                      initialInstructions={data_bot?.config.additional_instructions?.join(
                        "\n"
                      )}
                      onSubmit={(data) => {
                        handleModifyWidget(data);
                      }}
                      disabled={
                        entityIsLoading ||
                        callbotLoading ||
                        modifyCallbotLoading ||
                        entityData === undefined
                      }
                    />
                    <div className="self-stretch h-px bg-gray-200 block mt-6" />
                    <AutoPilotSettings
                      data={{
                        auto_pilot: res?.lmc_form?.auto_pilot,
                      }}
                      ownerLanguage={
                        processData
                          ? languageOfCountry(
                              processData.entity_data?.owner_data
                                ?.user_country_code
                            )
                          : undefined
                      }
                      onSubmit={handleModifyAutoPilot}
                    />
                  </div>
                )}
              </div>

              <div className="w-full h-px bg-gray-200 my-6" />
            </div>
          )}
        </div>
      </ResponsivePreviewLayout>
    );
  }

  return (
    <div className="h-[calc(100vh-65px)]">
      <Helmet>
        <title>Loyalty programs | Super User Manager</title>
        <meta
          name="description"
          content="Register to the easiest and most secure way to manage your users."
        />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/control-panel/lmc`}
        />
      </Helmet>

      <LouiseBotWidget
        botId="c44408c0-eaa2-4781-b5d6-1648eadb7ad9"
        mode="floating"
      />

      {content}

      <ModalShell
        size={"7xl"}
        show={showModal}
        onClose={() => setShowModal(false)}
        headerClassName="bg-gray-100"
        bodyClassName="bg-gray-100"
        title={
          <span className="block">
            <span className="mb-1 flex items-center gap-2 text-sm font-semibold text-gray-500">
              {t("dashboard.features.lmc.page.key1")}
              <HiChevronRight className="text-gray-300" />
              <span className="text-gray-900">
                {t("dashboard.features.lmc.page.key3")}
              </span>
            </span>
            {t("dashboard.features.lmc.page.key8")}
          </span>
        }
        subtitle={t("dashboard.features.lmc.page.key9")}
      >
        <div className="grid grid-cols-12 gap-4">
          {lmcAvailableTypes.map((lmc, index) => (
            <div
              className="lg:col-span-3 md:col-span-6 col-span-12 flex"
              key={index}
            >
              <div
                className={`px-4 pt-4 pb-5 bg-white rounded-lg border ${
                  selectedLmcForCreation === lmc.value
                    ? "border-blue-500"
                    : "border-gray-200 hover:border-gray-300"
                } flex-col gap-1 flex flex-1 place-content-between transition-colors ${
                  permissions.can(Cap.LOYALTY, Level.WRITE)
                    ? "cursor-pointer"
                    : "cursor-not-allowed opacity-60 pointer-events-none"
                }`}
                onClick={() => {
                  if (!permissions.can(Cap.LOYALTY, Level.WRITE)) return;
                  setSelectedLmcForCreation(lmc.value);

                  if (lmc.value) {
                    const newType = lmcLabelMapperReverse[
                      lmc.value
                    ] as lmcOptionType;

                    if (
                      !currentPlaygroundData ||
                      currentPlaygroundData.type !== newType
                    ) {
                      setCurrentPlaygroundData({
                        type: newType,
                        data: {
                          lmc_type: newType,
                          scanner_auto_refresh: false,
                          pre_defined_value: {
                            type: "",
                            values: [],
                            default: "",
                            currency: null,
                          },
                          comment: "",
                          package: 1,
                          options: {},
                        },
                      });
                    }
                  }

                  setShowModal(false);
                  setEditMode(true);
                }}
              >
                <div className="justify-start items-start">
                  <div className="items-start gap-2 inline-flex mb-1">
                    {lmc.icon}
                    <div className="text-base font-semibold text-gray-900">
                      {lmc.label}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">{lmc.description}</div>
                </div>

                <img
                  alt={lmc.label}
                  src={lmc.image}
                  className="mt-4 w-full h-36 bg-gray-800 rounded-md border border-gray-200 self-end"
                />
              </div>
            </div>
          ))}
        </div>
      </ModalShell>
    </div>
  );
};

export default LmcPage;
